import React, { useState, useRef, useEffect } from 'react';
import { useT, ENABLED_LANGS } from '../i18n/LanguageContext';

/**
 * Compact luxury language dropdown.
 * Variants:
 *  - `variant="dark"`   → glassy dark chip (default, used inside presentation & dashboard)
 *  - `variant="ghost"`  → transparent w/ hairline border (used on login page)
 */
export default function LanguageSwitcher({ variant = 'dark', className = '' }) {
  const { lang, setLang, langs } = useT();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const active = langs.find((l) => l.code === lang) || langs[0];

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={`lang-switcher lang-switcher--${variant} ${open ? 'is-open' : ''} ${className}`}
      data-testid="language-switcher"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="lang-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="language-trigger"
      >
        <span className="lang-trigger-flag" aria-hidden>{active.flag}</span>
        <span className="lang-trigger-code">{active.short}</span>
        <span className="lang-trigger-caret" aria-hidden>▾</span>
      </button>

      {open && (
        <ul role="listbox" className="lang-menu" data-testid="language-menu">
          {langs.map((l) => {
            const enabled = ENABLED_LANGS.includes(l.code);
            return (
              <li key={l.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={lang === l.code}
                  aria-disabled={!enabled}
                  disabled={!enabled}
                  onClick={() => { if (enabled) { setLang(l.code); setOpen(false); } }}
                  className={`lang-item ${lang === l.code ? 'is-active' : ''} ${enabled ? '' : 'is-disabled'}`}
                  data-testid={`language-option-${l.code}`}
                >
                  <span className="lang-item-flag" aria-hidden>{l.flag}</span>
                  <span className="lang-item-label">{l.label}</span>
                  <span className="lang-item-code">{l.short}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
